import { Injectable, computed, signal } from '@angular/core';
import { AuthServiceEntry, AuthSession, AuthUser } from '../models/auth.model';

export const ROLE_ADMIN = 'admin';
export const ROLE_EDITOR = 'editor';
export const ROLE_VIEWER = 'viewer';

export interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  club: string | null;
  clientName: string;
  tags: string[];
  services: AuthServiceEntry[];
  loading: boolean;
}

const INITIAL_STATE: AuthState = {
  isAuthenticated: false,
  user: null,
  club: null,
  clientName: '',
  tags: [],
  services: [],
  loading: false,
};

@Injectable({
  providedIn: 'root',
})
export class AuthStore {
  private readonly state = signal<AuthState>(INITIAL_STATE);

  public readonly isAuthenticated = computed(
    () => this.state().isAuthenticated
  );
  public readonly user = computed(() => this.state().user);
  public readonly club = computed(() => this.state().club);
  public readonly clientName = computed(() => this.state().clientName);
  public readonly tags = computed(() => this.state().tags);
  public readonly services = computed(() => this.state().services);
  public readonly loading = computed(() => this.state().loading);
  public readonly userEmail = computed(() => this.state().user?.email);
  public readonly userFullName = computed(() => this.state().user?.fullName);
  public readonly role = computed(() => this.state().tags[0] ?? null);

  public readonly canEdit = computed(
    () =>
      this.state().isAuthenticated &&
      (this.role() === ROLE_VIEWER ||
        this.role() === ROLE_EDITOR ||
        this.role() === ROLE_ADMIN)
  );

  public readonly isAdmin = computed(
    () => this.state().isAuthenticated && this.role() === ROLE_ADMIN
  );

  public setAuthenticated(session: AuthSession): void {
    this.state.set({
      isAuthenticated: true,
      user: session.user,
      club: session.club,
      clientName: session.clientName,
      tags: session.tags,
      services: session.services,
      loading: false,
    });
  }

  public setLoading(loading: boolean): void {
    this.state.update(state => ({ ...state, loading }));
  }

  public clearAuth(): void {
    this.state.set(INITIAL_STATE);
  }

  public getCurrentClient(): string | null {
    return this.state().club;
  }
}
